import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API } from "../lib/api";
import { Music } from "lucide-react";

/**
 * Public preview page for The Soundtrack.
 * Shows a sample of tracks + total count. No playlist URL, no notes.
 */
const SoundtrackPreview = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/soundtrack/preview`).then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) return <div className="p-12 text-center text-teal-200/70">Loading…</div>;

  return (
    <div className="fade-in max-w-3xl mx-auto px-6 py-16" data-testid="soundtrack-preview">
      <div className="text-xs uppercase tracking-[0.24em] text-teal-300/80 mb-4">Preview</div>
      <h1 className="font-display text-5xl mb-3">{data.title}</h1>
      <p className="font-display text-xl text-teal-200/80 mb-6">{data.subtitle}</p>
      <p className="text-neutral-400 leading-relaxed mb-10 max-w-2xl">{data.description}</p>

      <div className="card-surface p-6 mb-8">
        <div className="flex items-center gap-3 mb-4">
          <Music className="text-teal-400" size={18} />
          <div>
            <div className="text-xs uppercase tracking-widest text-neutral-500">
              {data.has_curated_samples ? "Sample tracks with notes" : "Sample tracks"}
            </div>
            <div className="text-sm text-neutral-300">
              {data.total_tracks > 0
                ? `Showing ${data.sample_tracks.length} of ${data.total_tracks} annotated tracks`
                : "The full track-by-track guide is being written now"}
            </div>
          </div>
        </div>
        {data.sample_tracks.length === 0 ? (
          <div className="text-sm text-neutral-500 py-8 text-center">
            The playlist itself is live — the commentary is being added in soon.
          </div>
        ) : (
          <ul className="space-y-3">
            {data.sample_tracks.map((t, i) => (
              <li key={i} className="pb-3 border-b border-neutral-800 last:border-0">
                <div className="flex items-baseline justify-between gap-4">
                  <div>
                    <span className="font-display text-lg text-neutral-100">{t.title}</span>
                    <span className="text-neutral-500 text-sm"> — {t.artist}</span>
                  </div>
                  <span className="text-xs uppercase tracking-widest text-teal-300/80">{t.mood}</span>
                </div>
                {t.note && (
                  <p className="text-sm text-neutral-300 italic mt-2">"{t.note}"</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="text-center">
        <p className="text-sm text-neutral-400 mb-4">
          Unlock the full soundtrack + track-by-track commentary + private playlist link.
        </p>
        <Link to="/studio" className="btn-primary px-6 py-3">Unlock for ${data.price}</Link>
      </div>
    </div>
  );
};

export default SoundtrackPreview;
